import { UpdateType } from "../../../backend/src/lib/types";
import { Background } from "./Background";
import { BulletType } from "./BulletType";
import { Engine } from "./Engine";
import { Entity, UpdateContext } from "./Entity";
import { Player } from "./Player";
import { Renderer } from "./Renderer";
import { L, log, logLevel } from "./log";

export class Bullet extends Entity {
  type: BulletType;
  parentType: BulletType;
  vx: number;
  vy: number;
  explosionState: number;
  owner: Player;
  state: Record<string, any>;
  ticks: number;
  constructor(
    engine: Engine,
    x: number,
    y: number,
    vx: number,
    vy: number,
    type: BulletType,
    owner: Player,
    parentType?: BulletType
  ) {
    super(engine, x, y);
    this.vx = vx;
    this.vy = vy;
    this.type = type;
    this.parentType = parentType ?? type;
    this.owner = owner;
    this.explosionState = 0;
    this.ticks = 0;
    this.state = {};
  }

  explosionTicks = () => {
    return Math.round(Math.min(10, this.type.explosionDamageSize));
  };

  explode = logLevel(L.INFO, (ctx: UpdateContext): void => {
    const { bg, localPlayer } = ctx;

    log(
      L.VERBOSE,
      `Exploded at x ${this.x} y ${this.y}, height at this x is ${
        bg.heightMap[Math.round(this.x)]
      }`
    );

    // Lazy way to handle edge wrapping
    for (const midX of [
      this.x,
      this.x + Background.WIDTH,
      this.x - Background.WIDTH,
    ]) {
      const bgCtx = bg.renderer.ctx;
      bgCtx.strokeStyle = "#000000";
      const dys = [];
      for (
        let dx = -this.type.explosionTerrainSize;
        dx <= this.type.explosionTerrainSize;
        dx++
      ) {
        const y = Math.round(
          Math.sqrt(
            Math.pow(this.type.explosionTerrainSize, 2) - Math.pow(dx, 2)
          )
        );
        const x = Math.round(midX + dx);
        let dy = 0;
        if (this.y - y >= bg.heightMap[x]) {
          dy = y * 2 + 1;
        } else {
          dy = Math.round(Math.max(0, this.y + y - bg.heightMap[x]));
        }
        dys.push(dy);
        bg.heightMap[x] += dy;
      }
      bg.refresh(
        Math.round(midX - this.type.explosionTerrainSize),
        Math.round(midX + this.type.explosionTerrainSize)
      );

      if (
        Math.pow(localPlayer.x - midX, 2) +
          Math.pow(localPlayer.y - this.y, 2) <
        Math.pow(this.type.explosionDamageSize, 2)
      ) {
        localPlayer.damage(this.type.damage, this.parentType.id, this.owner.id);
      }

      if (localPlayer === this.owner) {
        this.engine.remote.sendUpdate({
          type: UpdateType.BACKGROUND_UPDATE,
          x: Math.round(midX - this.type.explosionTerrainSize),
          data: dys,
        });
      }
    }

    this.type.explodeHook(this, this.engine);

    this.explosionState = 1;
    this.engine.schedule(this.engine.tick + this.explosionTicks(), () => {
      this.engine.bullets.despawnBullet(this);
    });
  });

  update(ctx: UpdateContext): void {
    this.ticks++;
    if (this.explosionState > 0) {
      this.explosionState++;
      return;
    }
    const { bg } = ctx;
    // Gravity
    this.vy += 0.1;

    // Apply velocity
    this.x += this.vx;
    this.y += this.vy;

    // Wrapping
    while (this.x < 0) {
      this.x += Background.WIDTH;
    }
    while (this.x > Background.WIDTH) {
      this.x -= Background.WIDTH;
    }

    this.type.tickHook(this, this.engine);

    // Collision
    if (bg.query(this.x, this.y)) {
      this.explode(ctx);
    }
  }

  render(r: Renderer): void {
    if (this.explosionState === 0) {
      r.ctx.fillStyle = "#FFFFFF";
      r.ctx.fillRect(this.x, this.y, 2, 2);
    } else {
      r.ctx.fillStyle = "#FF0000";
      // Lazy way to handle edge wrapping
      for (const midX of [
        this.x,
        this.x + Background.WIDTH,
        this.x - Background.WIDTH,
      ]) {
        r.ctx.beginPath();
        r.ctx.arc(
          midX,
          this.y,
          Math.sqrt(this.explosionState / this.explosionTicks()) *
            this.type.explosionDamageSize,
          0,
          2 * Math.PI
        );
        r.ctx.closePath();
        r.ctx.fill();
      }
    }
  }
}
